import { Agenda, Surface, Text } from '@shh/ui';
import { agendaItems } from '../../data/agenda';
import { calendarDay, type Layers } from '../../data/calendar';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { useSwipePane } from '../../lib/useSwipePane';
import { NextDays, TodayTodos } from './CompactLists';

/** One day's plan, exactly like the one on Heute – a swipe turns the day (Mike, 18.10.). */
export function DayPlan({
  f,
  date,
  now,
  lens,
  layers,
  people,
  onOpen,
}: {
  f: FamilyData;
  date: string;
  now: string;
  lens: string;
  layers: Layers;
  people?: string[];
  onOpen: (id: string, date: string) => void;
}) {
  const items = agendaItems(
    f,
    date,
    now,
    lens,
    (e) => (
      <>
        {e.transport && <span>{e.transport}</span>}
        {e.bring?.length ? <span>{calendarText.bring(e.bring.join(', '))}</span> : null}
      </>
    ),
    { list: calendarDay(f, date, { layers, people }) },
  ).map((i) => ({ ...i, dimmed: false }));
  if (!items.length)
    return (
      <Surface tone="sunken">
        <Text>{calendarText.noEvents}</Text>
      </Surface>
    );
  return (
    <div className="flex flex-col gap-gap-md">
      <Agenda
        items={items}
        pinAllDay
        nowLabel={date === f.today ? now : undefined}
        onItemPress={(id) => onOpen(id, date)}
      />
      {/* only all-day entries: say so, otherwise the chips look lost (Mike, 19.10.) */}
      {items.every((i) => !i.start) && (
        <Surface tone="sunken">
          <Text>{calendarText.onlyAllDay}</Text>
        </Surface>
      )}
    </div>
  );
}

export function DayView(props: Parameters<typeof DayPlan>[0] & { onStep: (dir: 1 | -1) => void }) {
  // the whole screen height swipes, not only the events (Mike, 19.10.)
  const { ref, style } = useSwipePane<HTMLDivElement>(props.onStep);
  // phones: the next seven days below the day, inside the swipe area (Mike, 19.10.: "Mobile
  // könnten wir unter der Tagesansicht auch noch die nächsten 7 Tage hinzufügen")
  return (
    // tablets and computers: always two columns – the next seven days and today's to-dos beside
    // the day (below on tablets upright). Mike, 19.10.: "In der Tagesansicht hat man den meisten
    // Platz, dort sollte es immer zweispaltig sein … Darunter auch die To-dos!"
    <div
      ref={ref}
      style={style}
      className="grid touch-pan-y grid-cols-1 gap-section max-md:min-h-svh lg:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <DayPlan {...props} />
      <div className="flex flex-col gap-section">
        <NextDays f={props.f} lens={props.lens} onOpen={props.onOpen} />
        <TodayTodos f={props.f} people={props.people} className="max-md:hidden" />
      </div>
    </div>
  );
}
